// imageCompress.js — Q-IMG-1 client-side image compression.
// Reduces upload payload before sending to Google Drive (and Supabase Edge
// Function 6MB body cap). Uses native Canvas API + createImageBitmap — zero
// new npm dependencies.
//
// Compression is an optimization, NEVER a blocker. On any failure we return
// the original file with skipped=true so the upload pipeline keeps going.

const SIZE_THRESHOLD_BYTES = 500 * 1024; // 500 KB — under this AND small dim → skip

/**
 * Compresses an image client-side via Canvas. Skips PDFs and non-image files.
 *
 * @param {File|Blob} file - Source file from <input type="file"> or drop.
 * @param {Object} [options]
 * @param {number} [options.maxDim=1600] - Max width/height (longest edge) in px.
 * @param {number} [options.quality=0.82] - JPEG quality 0..1 (WebP uses 0.78 internally).
 * @param {boolean} [options.preferWebP=true] - Try WebP encoding first; falls back to JPEG.
 * Note on `skipped`: true means the returned file is byte-identical to the
 * input (callers can re-use the original blob). For HEIC conversions we set
 * skipped:false even when canvas re-encode was bypassed, because the file
 * was still mutated (HEIC → JPEG) and callers need to know to use the new file.
 *
 * @returns {Promise<{file: Blob|File, originalSize: number, compressedSize: number, mimeType: string, skipped: boolean}>}
 */
export async function compressImage(file, options = {}) {
  const { maxDim = 1600, quality = 0.82, preferWebP = true } = options;
  const originalSize = file?.size ?? 0;
  let workingFile = file;
  let workingType = file?.type || '';
  const fileName = file?.name || '';

  // Skip PDFs and non-image inputs (HEIC mime falls under image/* so it passes).
  const isHeicByName = /\.(heic|heif)$/i.test(fileName);
  const isHeicByMime = /^image\/(heic|heif)$/i.test(workingType);
  const isHeic = isHeicByMime || isHeicByName;

  if (!file || workingType === 'application/pdf' || (!workingType.startsWith('image/') && !isHeicByName)) {
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      mimeType: workingType,
      skipped: true,
    };
  }

  // HEIC pre-pass: browsers can't decode HEIC in <canvas>/createImageBitmap.
  // Lazy-load heic-to only when needed. heic-to uses native HEIC decoding
  // on iOS Safari (where iPhone HEIC photos originate), with libheif WASM as
  // fallback for desktop browsers — much higher success rate than heic2any
  // for HEVC-encoded HEIC. If conversion still fails we return skipped:true
  // so the original .heic uploads to Drive (user keeps the file safe).
  if (isHeic) {
    try {
      const { heicTo } = await import('heic-to');
      const blob = await heicTo({
        blob: file,
        type: 'image/jpeg',
        quality: 0.85,
      });
      const jpegName = fileName.replace(/\.(heic|heif)$/i, '.jpg') || `image-${Date.now()}.jpg`;
      workingFile = new File([blob], jpegName, {
        type: 'image/jpeg',
        lastModified: file.lastModified || Date.now(),
      });
      workingType = 'image/jpeg';
    } catch (err) {
      console.warn('HEIC conversion failed, uploading original:', err);
      return {
        file,
        originalSize,
        compressedSize: originalSize,
        mimeType: file?.type || 'image/heic',
        skipped: true,
      };
    }
  }

  try {
    // Decode with EXIF orientation auto-applied (modern Chrome/Safari).
    const bitmap = await createImageBitmap(workingFile, { imageOrientation: 'from-image' });
    const longestEdge = Math.max(bitmap.width, bitmap.height);

    // Already small enough → skip recompression. Note: if HEIC was already
    // converted above, we still return the JPEG (workingFile), not the original.
    if (longestEdge <= maxDim && workingFile.size < SIZE_THRESHOLD_BYTES) {
      bitmap.close?.();
      return {
        file: workingFile,
        originalSize,
        compressedSize: workingFile.size,
        mimeType: workingType,
        skipped: !isHeic,
      };
    }

    const scale = longestEdge > maxDim ? maxDim / longestEdge : 1;
    const scaledW = Math.round(bitmap.width * scale);
    const scaledH = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = scaledW;
    canvas.height = scaledH;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.drawImage(bitmap, 0, 0, scaledW, scaledH);
    bitmap.close?.();

    let blob = null;
    if (preferWebP) {
      blob = await canvasToBlob(canvas, 'image/webp', 0.78);
    }
    let outMime = 'image/webp';
    if (!blob) {
      blob = await canvasToBlob(canvas, 'image/jpeg', quality);
      outMime = 'image/jpeg';
    }
    if (!blob) throw new Error('canvas.toBlob returned null for both WebP and JPEG');

    const ext = outMime === 'image/webp' ? 'webp' : 'jpg';
    const newName = renameWithExt(workingFile.name || `image-${Date.now()}`, ext);
    const outFile = new File([blob], newName, {
      type: blob.type || outMime,
      lastModified: workingFile.lastModified || Date.now(),
    });

    return {
      file: outFile,
      originalSize,
      compressedSize: outFile.size,
      mimeType: outFile.type,
      skipped: false,
    };
  } catch (err) {
    console.warn('Image compression failed, using original:', err);
    // If HEIC was converted but post-conversion compression failed, still
    // return the converted JPEG — it's better than the original .heic.
    return {
      file: workingFile,
      originalSize,
      compressedSize: workingFile.size,
      mimeType: workingType,
      skipped: !isHeic,
    };
  }
}

function canvasToBlob(canvas, mime, q) {
  return new Promise((resolve) => {
    try {
      canvas.toBlob((b) => resolve(b || null), mime, q);
    } catch (_) {
      resolve(null);
    }
  });
}

function renameWithExt(name, newExt) {
  const safe = String(name).replace(/[\r\n\\/]/g, '_');
  const dot = safe.lastIndexOf('.');
  const stem = dot > 0 ? safe.slice(0, dot) : safe;
  return `${stem}.${newExt}`;
}
